import { useState, useEffect } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Check, X, Users, Calendar, MapPin, Phone, Mail, Leaf, AlertTriangle, Download, Trash2, Printer } from 'lucide-react';
import { api } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const statusMap = {
  pending: { label: 'En attente', color: 'bg-[#D4AF37]/10 text-[#D4AF37]' },
  accepted: { label: 'Accepte', color: 'bg-[#5B6B3F]/10 text-[#5B6B3F]' },
  rejected: { label: 'Refuse', color: 'bg-[#8C3A3A]/10 text-[#8C3A3A]' },
};

export default function QuotesAdminPage() {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [historyActionLoading, setHistoryActionLoading] = useState(false);

  const load = () => {
    api.get(`/quotes${filter ? `?status=${filter}` : ''}`).then(r => setQuotes(r.data)).catch(() => {});
  };
  useEffect(load, [filter]);

  const updateStatus = async (id, status) => {
    try {
      await api.put(`/quotes/${id}`, { status, admin_note: note });
      if (['accepted', 'rejected'].includes(status)) {
        window.dispatchEvent(new CustomEvent('admin-request-resolved', {
          detail: { type: 'quote', referenceKey: 'quote_id', referenceId: id },
        }));
      }
      toast.success(`Devis ${status === 'accepted' ? 'accepte' : 'refuse'}`);
      setSelected(null); setNote(''); load();
    } catch { toast.error('Erreur'); }
  };

  const downloadBlob = (data, filename, contentType) => {
    const blob = new Blob([data], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const downloadAndDeleteHistory = async () => {
    if (!window.confirm("L'historique des devis sera d'abord téléchargé puis supprimé. Continuer ?")) {
      return;
    }
    setHistoryActionLoading(true);
    try {
      const exportResponse = await api.get('/quotes/history/export', { responseType: 'blob' });
      const disposition = exportResponse.headers['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/);
      const fileName = fileNameMatch?.[1] || 'historique-devis.xlsx';
      downloadBlob(exportResponse.data, fileName, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');

      const deleteResponse = await api.delete('/quotes/history');
      toast.success(`Historique téléchargé puis supprimé (${deleteResponse.data?.deleted_count || 0} devis)`);
      load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erreur lors du téléchargement ou de la suppression de l'historique");
    } finally {
      setHistoryActionLoading(false);
    }
  };

  const printQuote = async (quote) => {
    try {
      const response = await api.get(`/quotes/${quote.id}/print.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const printWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!printWindow) throw new Error('popup');
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      toast.error("Impossible d'ouvrir le devis a imprimer.");
    }
  };

  return (
    <div data-testid="admin-quotes-page">
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="font-heading text-2xl font-bold text-[#2A2A28]">Demandes de Devis</h1>
        <Button
          type="button"
          variant="outline"
          disabled={historyActionLoading}
          onClick={downloadAndDeleteHistory}
          className="rounded-xl border-[#8C3A3A]/20 text-[#8C3A3A] hover:bg-[#8C3A3A]/5"
          data-testid="quotes-download-delete-history"
        >
          {historyActionLoading ? <Download className="mr-2 h-4 w-4 animate-pulse" /> : <Trash2 className="mr-2 h-4 w-4" />}
          {historyActionLoading ? 'Traitement...' : "Télécharger puis supprimer l'historique"}
        </Button>
      </div>

      <div className="flex gap-2 mb-6 overflow-x-auto">
        {['', 'pending', 'accepted', 'rejected'].map(s => (
          <Button key={s} variant={filter === s ? 'default' : 'outline'} size="sm"
            className={`rounded-full ${filter === s ? 'bg-[#5B6B3F] text-white' : 'border-[#EBE8E0] text-[#7A7A75]'}`}
            onClick={() => setFilter(s)} data-testid={`quote-filter-${s || 'all'}`}>
            {s === '' ? 'Toutes' : statusMap[s]?.label}
          </Button>
        ))}
      </div>

      <div className="space-y-3">
        {quotes.map(q => (
          <div key={q.id} className="bg-white rounded-xl border border-[#EBE8E0] p-4" data-testid={`quote-${q.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Badge className={`text-xs ${statusMap[q.status]?.color || ''}`}>{statusMap[q.status]?.label}</Badge>
                  <span className="text-xs text-[#7A7A75]">{new Date(q.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
                <p className="text-xs text-[#5B6B3F] font-medium">Ref: {q.id}</p>
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-[#5B6B3F]" /> {q.guests} pers.</span>
                  <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-[#5B6B3F]" /> {q.event_date}</span>
                  <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-[#5B6B3F]" /> {q.event_address}</span>
                </div>
                <div className="flex items-center gap-4 text-sm flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-[#7A7A75]" /> {q.email}</span>
                  <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-[#7A7A75]" /> {q.phone}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  {q.normal && <Badge variant="outline" className="border-[#5B6B3F]/20 text-[#5B6B3F]">Classique</Badge>}
                  {q.vegetarian && <Badge variant="outline" className="border-green-500/20 text-green-600"><Leaf className="w-3 h-3 mr-0.5" />Vegetarien</Badge>}
                  {q.allergies && <Badge variant="outline" className="border-[#D4AF37]/20 text-[#D4AF37]"><AlertTriangle className="w-3 h-3 mr-0.5" />{q.allergies}</Badge>}
                </div>
                {q.notes && <p className="text-xs text-[#7A7A75] italic">"{q.notes}"</p>}
              </div>
              <div className="flex gap-2 flex-shrink-0">
                <Button size="sm" variant="outline" className="rounded-lg border-[#1E2D3D]/20 text-[#1E2D3D]" onClick={() => printQuote(q)} data-testid={`print-quote-${q.id}`}>
                  <Printer className="w-3.5 h-3.5 mr-1" /> Imprimer
                </Button>
                {q.status === 'pending' && (
                  <>
                    <Button size="sm" className="rounded-lg bg-[#5B6B3F] hover:bg-[#4A5832] text-white" onClick={() => updateStatus(q.id, 'accepted')} data-testid={`accept-quote-${q.id}`}>
                      <Check className="w-3.5 h-3.5 mr-1" /> Accepter
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-lg border-[#8C3A3A]/20 text-[#8C3A3A]" onClick={() => { setSelected(q); setNote(''); }} data-testid={`reject-quote-btn-${q.id}`}>
                      <X className="w-3.5 h-3.5 mr-1" /> Refuser
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
        {quotes.length === 0 && <p className="text-center text-[#7A7A75] py-8">Aucune demande de devis</p>}
      </div>

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">Refuser le devis</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-[#7A7A75]">Devis de {selected?.guests} personnes le {selected?.event_date}</p>
            <div><Label className="text-sm">Motif (optionnel)</Label><Textarea className="mt-1 rounded-xl border-[#EBE8E0]" value={note} onChange={e => setNote(e.target.value)} /></div>
            <Button onClick={() => updateStatus(selected?.id, 'rejected')} className="w-full rounded-xl bg-[#8C3A3A] hover:bg-[#7a3232] text-white" data-testid="confirm-reject-quote">Confirmer le refus</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
